import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { LayoutDashboard, Hammer, Home, FileText, ShieldCheck, Wrench, Megaphone, LogOut, User } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "./ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/build", label: "Build", icon: Hammer },
  { to: "/home", label: "My Home", icon: Home },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/warranty", label: "Warranty", icon: ShieldCheck },
  { to: "/service", label: "Service", icon: Wrench },
  { to: "/updates", label: "Updates", icon: Megaphone },
];

export function HomeownerLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "H").split(" ").map((s) => s[0]).slice(0, 2).join("");

  return (
    <div className="min-h-screen bg-arq-bg">
      {/* Top nav (desktop) */}
      <header className="sticky top-0 z-40 bg-arq-card/75 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <button onClick={() => navigate("/dashboard")} className="flex items-center" data-testid="brand-logo">
            <img src="/arqitrak-logo.png" alt="ARQITRAK" className="h-6 w-auto" />
          </button>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
                className={({ isActive }) => `px-3 py-2 text-sm rounded-md transition-colors ${isActive ? "bg-arq-green text-arq-bg" : "text-arq-ink hover:bg-arq-surface"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-menu" className="h-9 w-9 rounded-full bg-arq-green text-arq-bg text-xs font-semibold flex items-center justify-center uppercase">
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                  <User className="h-4 w-4" /> Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-28 lg:pb-12">{children}</main>

      {/* Bottom tab bar (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-arq-card/90 backdrop-blur-xl border-t border-white/10 flex items-stretch justify-around px-1 pb-[env(safe-area-inset-bottom)]">
        {NAV.slice(0, 5).map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} data-testid={`tab-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2 flex-1 text-[10px] ${isActive ? "text-arq-green" : "text-muted-foreground"}`}>
              <Icon className="h-5 w-5" strokeWidth={1.6} />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
